<template>
    <div>
        <div class="row">
            <div class="col-12 col-md-6 col-lg-5" v-if="$isAllowed('viewAllOrders')">
                <date-range-picker @starttimechanged="startTime = $event"
                                   @endtimechanged="endTime = $event">
                </date-range-picker>
            </div>
            <div class="col-12 col-md-6 col-lg-7">
                <div class="ml-md-auto mt-2">
                    <label for="search" class="ml-0 ml-md-auto mr-2 d-block d-md-inline">
                        {{$t('ORDERS.searchOrderId')}}
                    </label>
                    <input type="text"
                           class="search-input ml-0 mt-2 mt-lg-0 ml-md-auto d-block d-md-inline"
                           name="search"
                           id="search"
                           v-model="searchTerm"
                           @input="searchOrder">
                </div>
            </div>
        </div>
        <table  class="table table-striped table-responsive-md">
            <thead>
            <tr>
                <th>#</th>
                <table-head-with-sort :text="$t('COMMON.orderId')"
                                         :sortType="'id'"
                                         @sort="handleSort"/>
                <table-head-with-sort :text="$t('COMMON.date')"
                                         :sortType="'date'"
                                         @sort="handleSort"/>
                <table-head-with-sort :text="$t('COMMON.photosOrdered')"
                                         :sortType="'photosOrdered'"
                                         @sort="handleSort"/>
                <table-head-with-sort :text="$t('COMMON.totalPrice')"
                                         :sortType="'totalPrice'"
                                         @sort="handleSort"/>
                <table-head-with-sort :text="$t('COMMON.volume')"
                                         :sortType="'volume'"
                                         @sort="handleSort"/>
                <th></th>
            </tr>
            </thead>
            <tbody v-if="orders.length > 0">
            <tr v-for="(order, index) in sortedOrders"
                :key="order.id"
                :class="['table-row', {'row-active': order.id === orderSelected.id}]"
                @click="$emit('update:orderSelected', order)"
                v-if="(index < (currentPage - 1) * 10 + 10 && index >= (currentPage - 1 ) * 10) && (typeof order === 'object')">
                <td scope="row"><strong>{{index}}</strong></td>
                <td>{{order.id}}</td>
                <td>{{$utils.moment(order.timestamp).format('YYYY-MM-DD')}}</td>
                <td>{{getProductQuantityFromCart(order.cart)}}</td>
                <td>{{getCartPrice(order.cart, prices).toFixed(2)}} €</td>
                <td>{{order.volume ? `${(order.volume).toFixed(1)} MB` : 'N/A'}}</td>
                <td class="table-button-container">
                    <button class="btn icon-btn"
                            @click.stop="confirmAction({
                                question: $t('ORDERS.printReceipt'),
                                data: order, callback: repeatPrint
                            })"
                            name="repeat receipt"
                            data-toggle="tooltip"
                            data-placement="top"
                            :title="$t('ORDERS.printReceipt')"
                            v-if="$isAllowed('update')">
                        <i class="icon icon-cph_iconset_druckersymbol"></i>
                    </button>
                    <button class="btn icon-btn"
                            @click.stop="confirmAction({
                                question: $t('ORDERS.repeatOrder'),
                                data: order, callback: printOrder
                            })"
                            name="print order"
                            data-toggle="tooltip"
                            data-placement="top"
                            :title="$t('ORDERS.repeatOrder')"
                            v-if="$isAllowed('update')">
                        <i class="icon icon-cph_iconset_auftragswiederholung"></i>
                    </button>
                    <button class="btn icon-btn" @click.stop="confirmAction({
                                question: $t('ORDERS.deleteOrder'),
                                data: order, callback: deleteOrder
                            })"
                            name="delete order"
                            data-toggle="tooltip"
                            data-placement="top"
                            :title="$t('ORDERS.deleteOrder')"
                            v-if="$isAllowed('destroy')">
                        <i class="icon icon-cph_iconset_trash_can"></i>
                    </button>
                </td>
            </tr>
            </tbody>
            <tbody v-else>
            <tr>
                <td colspan="6">
                    {{searchTerm ? $t('ORDERS.noOrdersBySearch') : $t('ANALYTICS.noOrders')}}
                </td>
            </tr>
            </tbody>
        </table>
        <pagination :currPage.sync="currentPage"
                    :pagesLength="calculateNumberOfPages()"></pagination>
    </div>
</template>

<script type="text/babel">
import DateRangePicker from '../../../Shared/Datepicker/DateRangePicker.vue';
import {OrdersPerimeter} from '../../../../router/perimeters/ordersPerimeter';
import Pagination from '../../../Shared/CphPagination.vue';
import AnalyticsService from '../../Analytics/AnalyticsService';
import {mapActions, mapGetters, mapMutations} from 'vuex';
import TableHeadWithSort from '../../../Shared/UI/Table/TableHeadWithSort';

export default {
    name: 'OrdersTable',
    data () {
        return {
            currentPage: 1,
            alertVisible: false,
            deletedOrderId: '',
            searchedOrders: [],
            searchTerm: '',
            sortObj: {
                mode: 'asc',
                sortType: ''
            }
        };
    },
    perimeters: [OrdersPerimeter],
    props: {
        orders: {
            type: Array,
            required: true,
            default: []
        },
        orderSelected: {
            type: Object
        }
    },
    components: {
        TableHeadWithSort,
        Pagination,
        DateRangePicker
    },
    mixins: [AnalyticsService],
    computed: {
        ...mapGetters({
            prices: 'getAllPrices'
        }),
        displayedOrders () {
            if (this.searchTerm) {
                return this.searchedOrders;
            } else {
                return this.orders;
            }
        },
        sortedOrders () {
            let {mode, sortType} = this.sortObj;
            const asc = mode === 'asc';
            if(sortType === 'date') {
                return this.displayedOrders.sort( (a, b) => this.getTimeStamp(asc ? a.timestamp : b.timestamp) - this.getTimeStamp(asc ? b.timestamp : a.timestamp));
            } else if(sortType === 'id') {
                return this.displayedOrders.sort( (a, b) => {
                    let left = asc ? a[sortType].toLowerCase() : b[sortType].toLowerCase();
                    let right = asc ? b[sortType].toLowerCase() : a[sortType].toLowerCase();
                    return left < right ? -1 : 1;
                });
            } else if(sortType === 'volume') {
                return this.displayedOrders.sort( (a, b) => (asc ? a[sortType] : b[sortType]) - (asc ? b[sortType] : a[sortType]));
            } else if(sortType === 'photosOrdered') {
                return this.displayedOrders.sort( (a, b) => this.getProductQuantityFromCart(asc ? a.cart : b.cart) - this.getProductQuantityFromCart(asc ? b.cart : a.cart));
            } else if(sortType === 'totalPrice') {
                return this.displayedOrders.sort( (a, b) => this.getCartPrice(asc ? a.cart : b.cart, this.prices) - this.getCartPrice(asc ? b.cart : a.cart, this.prices));
            }
            return this.displayedOrders;
        }
    },
    methods: {
        ...mapActions([
            'deleteOrderFromDatabase',
            'repeatPrintAsync',
            'getOrderListAsync',
            'printOrderAsync',
            'confirmAction',
            'getOrderByIdAsync'
        ]),
        ...mapMutations([
            'addAlert'
        ]),
        handleSort (sortObj) {
            this.sortObj = sortObj;
        },
        getTimeStamp (date) {
            let dateObj = new Date(date);
            return dateObj.getTime();
        },
        async searchOrder () {
            this.currentPage = 1;
            try {
                let order = await this.getOrderByIdAsync(this.searchTerm);
                this.searchedOrders = [order];
            } catch (err) {
                this.searchedOrders = [];
            }
        },
        calculateNumberOfPages () {
            return Math.ceil(this.orders.length / 10);
        },
        deleteOrder (order) {
            if(order.id === this.orderSelected.id) {
                this.$emit('closeDetails');
            }
            this.deletedOrderId = order.id;
            let newOrderList = [...this.orders];
            newOrderList.splice(newOrderList.indexOf(order), 1);
            this.deleteOrderFromDatabase({orderID: order.id, orderList: newOrderList});
        },
        repeatPrint (order) {
            this.repeatPrintAsync({orderID: order.id});
        },
        printOrder (order) {
            this.printOrderAsync({orderID: order.id});
        }
    }
};

</script>
